import type { Note, NoteSeverity, NoteStage } from "../types.ts";
import { higherSeverity } from "./severity.ts";

export type SeverityCounts = Record<NoteSeverity, number>;

export function isOpen(note: Note): boolean {
	return note.resolvedAt === undefined;
}

export function openBySeverity(notes: Note[]): SeverityCounts {
	const counts: SeverityCounts = { info: 0, warning: 0, critical: 0 };
	for (const note of notes) {
		if (isOpen(note)) counts[note.severity] += 1;
	}
	return counts;
}

export function openTotal(counts: SeverityCounts): number {
	return counts.info + counts.warning + counts.critical;
}

export function highestSeverityByStage(
	notes: Note[],
	stages: NoteStage[],
): Map<string, NoteSeverity> {
	const parents = new Map(stages.map((stage) => [stage.id, stage.parentId]));
	const result = new Map<string, NoteSeverity>();
	for (const note of notes) {
		if (!isOpen(note) || !note.stageId || !parents.has(note.stageId)) continue;
		let cursor: string | undefined = note.stageId;
		while (cursor) {
			result.set(cursor, higherSeverity(result.get(cursor), note.severity));
			cursor = parents.get(cursor);
		}
	}
	return result;
}

export function generalOpen(notes: Note[]): Note[] {
	return notes
		.filter((note) => isOpen(note) && !note.stageId)
		.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function subtreeIds(stages: NoteStage[], rootId: string): Set<string> {
	const children = new Map<string, string[]>();
	for (const stage of stages) {
		if (!stage.parentId) continue;
		const list = children.get(stage.parentId) ?? [];
		list.push(stage.id);
		children.set(stage.parentId, list);
	}
	const ids = new Set<string>([rootId]);
	const queue = [rootId];
	while (queue.length > 0) {
		const current = queue.pop() as string;
		for (const child of children.get(current) ?? []) {
			if (ids.has(child)) continue;
			ids.add(child);
			queue.push(child);
		}
	}
	return ids;
}
